import { AxiosError, HttpStatusCode } from "axios";
import { deleteApi, getApi, postApi, putApi } from "./helpers/apiMethods";
import ResourceInUseError from "../errors/ResourceInUseError";
import { SavedSearch } from "../types/saved-search/SavedSearch";
import { GetInteractionsRequest } from "./interaction";

const path = "saved-searches";

export interface SavedSearchResponse {
  savedSearch: SavedSearch;
}

export interface SavedSearchModel {
  customerId: string;
  savedSearchId: string;
  name: string;
}

export interface GetAllSavedSearchResponse {
  savedSearches: SavedSearchModel[];
}

export interface CreateSavedSearchRequest {
  customerId: string;
  name: string;
  searchRequest: GetInteractionsRequest;
}

export interface UpdateSavedSearchRequest {
  customerId: string;
  savedSearchId: string;
  name: string;
  searchRequest: GetInteractionsRequest;
}

export const getSavedSearch = async (
  customerId: string,
  savedSearchId: string
): Promise<SavedSearch | undefined> => {
  const response = await getApi<SavedSearchResponse>(
    `/v1/customers/${customerId}/${path}/${savedSearchId}`
  );
  return response.data?.savedSearch;
};

export const getAllSavedSearches = async (
  customerId: string
): Promise<SavedSearchModel[]> => {
  const response = await getApi<GetAllSavedSearchResponse>(
    `/v1/customers/${customerId}/${path}`
  );

  const savedSearches = response.data?.savedSearches.map(
    (searchResponse) => searchResponse
  );
  return savedSearches ?? [];
};

export const createSavedSearch = async ({
  customerId,
  ...request
}: CreateSavedSearchRequest): Promise<SavedSearch | undefined> => {
  const response = await postApi<SavedSearchResponse>(
    `/v1/customers/${customerId}/${path}`,
    request
  );
  return response.data?.savedSearch;
};

export const updateSavedSearch = async ({
  customerId,
  savedSearchId,
  ...request
}: UpdateSavedSearchRequest): Promise<SavedSearch | undefined> => {
  const response = await putApi<SavedSearchResponse>(
    `/v1/customers/${customerId}/${path}/${savedSearchId}`,
    request
  );
  return response.data?.savedSearch;
};

export const deleteSavedSearch = async (
  customerId: string,
  savedSearchId: string
): Promise<void> => {
  try {
    await deleteApi<void>(
      `/v1/customers/${customerId}/${path}/${savedSearchId}`
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const statusCode =
        error.status ??
        error.response?.status ??
        HttpStatusCode.InternalServerError;
      if (statusCode === HttpStatusCode.Conflict) {
        throw new ResourceInUseError(
          "This saved search is in use and cannot be deleted."
        );
      }
    }
    throw error;
  }
};
